import { forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { Search, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePropertyKitCategories, usePropertyKitItems, CONTROL_TYPE_LABELS, PropertyKitControlType } from '@/hooks/inventory';
import ItemsTable from '../tables/ItemsTable';
import ItemFormModal from '../modals/ItemFormModal';
import CategoryFormModal from '../modals/CategoryFormModal';

export interface ItemsCatalogViewRef {
  openCreateModal: () => void;
  openCategoriesModal: () => void;
}

const ItemsCatalogView = forwardRef<ItemsCatalogViewRef>((_, ref) => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [controlTypeFilter, setControlTypeFilter] = useState<string>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);

  const { categories } = usePropertyKitCategories();
  const { items, isLoading, reorderItem, isReordering } = usePropertyKitItems({
    categoryId: categoryFilter !== 'all' ? categoryFilter : undefined,
    controlType: controlTypeFilter !== 'all' ? controlTypeFilter as PropertyKitControlType : undefined,
    search: search || undefined,
  });

  useImperativeHandle(ref, () => ({
    openCreateModal: () => setIsCreateModalOpen(true),
    openCategoriesModal: () => setIsCategoryModalOpen(true),
  }));

  // Group items by category
  const itemsByCategory = useMemo(() => {
    const grouped = new Map<string, typeof items>();
    
    items.forEach((item) => {
      const categoryId = item.category?.id || 'uncategorized';
      const categoryName = item.category?.name || 'Sin categoría';
      const sortOrder = item.category?.sort_order ?? 999;
      const key = `${sortOrder}|||${categoryId}|||${categoryName}`;
      
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(item);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => {
        const [sortA] = a[0].split('|||');
        const [sortB] = b[0].split('|||');
        return Number(sortA) - Number(sortB);
      })
      .map(([key, categoryItems]) => {
        const [, categoryId, categoryName] = key.split('|||');
        // Items are already sorted by sort_order from the query
        return { categoryId, categoryName, items: categoryItems };
      });
  }, [items]);

  const handleEditItem = (itemId: string) => {
    setEditingItem(itemId);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingItem(null);
  };

  const handleReorderItem = async (
    itemId: string,
    swapItemId: string,
    newSortOrder: number,
    swapSortOrder: number
  ) => {
    await reorderItem({
      itemId,
      swapItemId,
      newSortOrder,
      swapSortOrder,
    });
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar artículo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-full bg-gray-100 border-0"
          />
        </div>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px] rounded-full bg-gray-100 border-0">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={controlTypeFilter} onValueChange={setControlTypeFilter}>
          <SelectTrigger className="w-[180px] rounded-full bg-gray-100 border-0">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {Object.entries(CONTROL_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-brand-primary border-t-transparent rounded-full" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No hay artículos en el catálogo</p>
          <Button
            variant="link"
            onClick={() => setIsCreateModalOpen(true)}
            className="mt-2"
          >
            Agregar el primero
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {itemsByCategory.map(({ categoryId, categoryName, items: categoryItems }) => (
            <div key={categoryId} className="space-y-2">
              <h3 className="font-medium text-sm text-muted-foreground">
                {categoryName} ({categoryItems.length})
              </h3>
              <ItemsTable
                items={categoryItems}
                onEdit={handleEditItem}
                onReorder={handleReorderItem}
                isReordering={isReordering}
              />
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <ItemFormModal
        open={isCreateModalOpen}
        onOpenChange={handleCloseModal}
        editingItemId={editingItem}
      />

      <CategoryFormModal
        open={isCategoryModalOpen}
        onOpenChange={setIsCategoryModalOpen}
      />
    </div>
  );
});

ItemsCatalogView.displayName = 'ItemsCatalogView';

export default ItemsCatalogView;
